import { branchMissingMapLocation } from '../../settings/domain/branchLocationStatus';
import type { StoreProfile } from '../../settings/domain/storeProfileTypes';
import { getCachedStoreProfile } from '../../settings/application/storefrontStatus';
import type { BranchAccess } from '../../shell/session/store/sessionTypes';

/** Subconjunto del perfil que la evidencia de activación necesita (RPC parcial lo satisface). */
export type StoreProfileActivationFields = Pick<
  StoreProfile,
  'isPublic' | 'businessHours' | 'deliveryEnabled' | 'ownDeliveryEnabled' | 'vueltoRidersEnabled'
>;

export type StoreActivationComputed = {
  locationDone: boolean;
  hoursDone: boolean;
  deliveryDone: boolean;
  onlineDone: boolean | null;
  requiredDone: boolean;
  isPublic: boolean;
  /** Una tienda publicada no necesita el checklist, aunque el catálogo local aún no esté hidratado. */
  activationComplete: boolean;
};

export async function readStorePublished(branchId: string): Promise<boolean | null> {
  try {
    const profile = await getCachedStoreProfile(branchId);
    return profile?.isPublic === true;
  } catch {
    return null;
  }
}

export function hoursConfigured(profile: StoreProfileActivationFields | null): boolean {
  return Boolean(profile?.businessHours?.some((d) => d.isOpen && d.shifts.length > 0));
}

export function computeDeliveryDone(
  hasDelivery: boolean,
  profile: StoreProfileActivationFields | null,
  zonesCount: number,
): boolean {
  return (
    !hasDelivery ||
    !profile?.deliveryEnabled ||
    zonesCount > 0 ||
    Boolean(profile?.ownDeliveryEnabled) ||
    Boolean(profile?.vueltoRidersEnabled)
  );
}

export function computeStoreActivation(
  branch: BranchAccess | undefined,
  profile: StoreProfileActivationFields | null,
  zonesCount: number,
  onlineCount: number | null,
  hasDelivery: boolean,
): StoreActivationComputed {
  const locationDone = branch ? !branchMissingMapLocation(branch) : false;
  const hoursDone = hoursConfigured(profile);
  const deliveryDone = computeDeliveryDone(hasDelivery, profile, zonesCount);
  const onlineDone = onlineCount === null ? null : onlineCount > 0;
  const requiredDone = locationDone && hoursDone && deliveryDone && onlineDone === true;
  const isPublic = Boolean(profile?.isPublic);
  return {
    locationDone,
    hoursDone,
    deliveryDone,
    onlineDone,
    requiredDone,
    isPublic,
    activationComplete: isPublic,
  };
}

